import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#0891b2'; // Cyan-600
  const lastUpdated = 'December 30, 2025';

  return (
    <LegalLayout app="Hydrate Daily" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the Hydrate Daily mobile application
                (the "App"). Hydrate Daily is a simple, privacy-focused water intake tracker designed
                to help you stay hydrated throughout your day.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use our App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Hydrate Daily is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Zero Third-Party SDKs:</strong> We use only Apple's native frameworks. No analytics, tracking, or advertising SDKs are included.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration or login.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics or behavioral data.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Network Requests:</strong> The App makes no network connections. All data stays on your device.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>100% Offline:</strong> The App works completely offline with no internet required.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Collect
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Water Intake Data
              </h3>
              <p className="mb-4 leading-relaxed">
                Hydrate Daily stores the following data locally on your device:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Daily water intake amounts (in milliliters)</li>
                <li>Your personal daily hydration goal</li>
                <li>Hydration history and streak data</li>
                <li>Timestamps of when water was logged</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> All hydration
                data is stored locally on your device using iOS's built-in UserDefaults system and
                is never transmitted to any server.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Notification Preferences
              </h3>
              <p className="leading-relaxed">
                If you enable hydration reminders, we store your notification preferences locally,
                including reminder intervals and active hours. These settings are stored on your
                device and are not transmitted elsewhere.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage & Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by iOS's built-in security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage Only:</strong> All data is stored locally using iOS's UserDefaults system, protected by your device's encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Device-Level Security:</strong> Your data is protected by your device's passcode, Face ID, or Touch ID.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Cloud Storage:</strong> We do not sync or backup your data to any cloud service.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Server Communication:</strong> The App never connects to any external servers.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h2>
              <p className="mb-4 leading-relaxed">
                Hydrate Daily offers optional hydration reminders. If you choose to enable notifications:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Notifications are scheduled locally using iOS's built-in notification system.</li>
                <li>No notification data is sent to external servers.</li>
                <li>You can customize reminder frequency and active hours.</li>
                <li>Notifications can be disabled at any time in the App settings or iOS Settings.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Hydrate Daily does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Location data</li>
                <li>Contact information</li>
                <li>Health data from HealthKit</li>
                <li>Device identifiers</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
                <li>Any data to external servers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                Hydrate Daily uses <strong style={{ color: 'var(--text-primary)' }}>only Apple's native frameworks</strong>:
                SwiftUI, UserNotifications, and Foundation. We do not integrate any third-party SDKs,
                analytics tools, crash reporters, or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights & Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View:</strong> Access all your hydration history within the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Reset:</strong> Reset your daily intake at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Modify:</strong> Change your daily goal whenever you want.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Uninstalling the App removes all stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Hydrate Daily is suitable for users of all ages as it does not collect any personal
                information. The App can be used by anyone to track their hydration without any
                privacy concerns.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/hydratedaily/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Hydrate Daily Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
